/**
 * control-table-modal.component.ts
 * Copyright (c) 2017, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Component, EventEmitter, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { DialogRef, ModalComponent } from "angular2-modal";
import { BSModalContext } from "angular2-modal/plugins/bootstrap";
import { BuildFlags } from "../../configuration/build-flags";
import { TabularControlGroup } from "./control-group";
import { TypedDataHolder } from "./typed-data";

export interface ControlTableContext extends BSModalContext
{
    group : TabularControlGroup;
    tunnel : (event : Event, step : TypedDataHolder) => void;
    act : (name : string, args? : any) => void;
}

@Component({
  template: `
<div class="modal-header">
    <button
      type="button"
      class="close"
      (click)="dialog.dismiss()"
      aria-label="Close">
      <span aria-hidden="true">&times;</span>
    </button>
    <h3 class="capitalized modal-title">
        {{group.name}}
    </h3>
</div>


<table class="table">
    <thead>
        <th *ngFor="let step of group.steps">
            {{step.hints.column}}
        </th>
    </thead>
    <tbody class="collapsed-statuses">
        <tr *repeatTo="group.size; let i = index">
            <td
                *ngFor="let step of group.steps"
                class="intake-step"
                [class.bg-danger]="i >= step.control.length">
                <typed-field
                    *ngIf="i < step.control.length"
                    [input]="step"
                    [control]="step.control.controls[i]"
                    [clearButton]="false"
                    [iconTray]="false"
                    [validationTray]="false"
                    (change)="change($event, step)">
                </typed-field>
                <span *ngIf="i >= step.control.length">
                    No value at index {{i}}
                </span>
            </td>
            <td *ngIf="rowActions && group.enabled" class="btn-group">
                <button
                    *ngIf="i !== 0"
                    class="btn btn-default"
                    alt="Move up"
                    [style.width]="(i === group.size-1) ? '79px' : 'auto'"
                    (click)="act('moveRow', [i, -1])">
                    <span class="glyphicon glyphicon-chevron-up"></span>
                </button>
                <button
                    *ngIf="i !== group.size-1"
                    class="btn btn-default"
                    alt="Move down"
                    (click)="act('moveRow', [i, 1])"
                    [style.width]="(i === 0) ? '79px' : 'auto'">
                    <span
                        class="glyphicon glyphicon-chevron-down">
                    </span>
                </button>
                <button
                    class="btn btn-danger"
                    alt="Remove this entry"
                    [disabled]="group.size === 1"
                    (click)="act('removeRow', [i])">
                    <span class="glyphicon glyphicon-remove"></span>
                </button>
            </td>
        </tr>
    </tbody>
    <tfoot *ngIf="showComments">
        <tr>
            <td *ngFor="let step of group.steps">
                ` + (BuildFlags.debugMode ?
                    `<div class="debug-buttons">
                        <button
                            type="button"
                            class="btn btn-default debug-only"
                            (click)="act('explain',step)"
                            tabindex="-1">
                            Explain
                        </button>
                        <button
                            type="button"
                            class="btn btn-default debug-only"
                            (click)="act('details',step)"
                            tabindex="-1">
                            Details
                        </button>
                    </div>` : "") + `
            </td>
        </tr>
    </tfoot>
</table>

<div class="modal-body" *ngIf="group.form.invalid">
    <h3>Validation Issues</h3>
    <group-validation
        [group]="group.form"
        class="text-danger">
    </group-validation>
</div>

<div class="modal-body">
    <h3>Comments </h3>
    <comment-list
        *ngFor="let step of group.steps"
        [comments]="step.comments"
        [parent]="step"
        [showNewForm]="false">
    </comment-list>
    <comment-list
        *ngIf="group.commentStep"
        [comments]="group.commentStep.comments"
        [parent]="group.commentStep"
        [showComments]="false">
    </comment-list>
    <span *ngIf="!group.commentStep" class="text-warning">
        <strong>Note:</strong>
        Comments are disabled for this table because it has no column annotated
        as the comment "host". A subject matter expert must add the category
        <code>hint-comment-column</code> to exactly 1 column.
    </span>
</div>


<div class="modal-footer">
    <button
        type="button"
        class="btn btn-default"
        (click)="dialog.close()">
        Close
    </button>
</div>
    `,
  styles: [
      `.table {
          width: 100%;
          padding: 5px;
        }
        th {
            padding: 4px 6px;
            vertical-align: top;
        }
        th >>> p:last-child:not(:first-child) {
            margin-bottom: 0;
            font-weight: normal;
        }
        .modal-body {
            border-top: 1px solid #EEE;
        }
        h3, h5 {
            margin-top: 0;
        }
        group-validation >>> div {
            margin-left: 1em;
        }`
  ]
})
export class ControlTableModalComponent
  implements ModalComponent<ControlTableContext>, OnInit
{
    public readonly rowActions : boolean = true;

    public showComments : boolean = false;

    public commentStep : TypedDataHolder;

    public group : TabularControlGroup;

    constructor (
        public dialog : DialogRef<ControlTableContext>,
    ) {}

    public ngOnInit () : void
    {
        this.group = this.dialog.context.group;
    }

    public change (event : Event, step : TypedDataHolder) : void
    {
        // debugger;
        if (!step.control.value)
        {
            console.warn(step.control.value);
        }
        step.control.patchValue(step.control.value);
        this.dialog.context.tunnel(event, step);
    }

    public act (name : string, args? : any) : void
    {
        this.dialog.context.act(name, args);
    }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/common/data/control-table-modal.component.ts